import { Component, Inject, OnInit } from '@angular/core';
import { ConfigurationInterface, WhitelabelService } from '@galaxy/partner';
import { FeatureFlagService } from '@vendasta/businesses';
import { combineLatest, Observable } from 'rxjs';
import { filter, map, publishReplay, refCount, switchMap } from 'rxjs/operators';

const MARKETING_AUTOMATION = 'marketing-automation';
const CUSTOMIZE_SNAPSHOT = 'snapshot-report-customization';
const SALES_ORDERS = 'sales-orders';

interface SnapshotSettingInterface {
  textKey: string;
  url?: string;
  currentValue: string;
}

@Component({
  selector: 'app-sales-snapshot-settings',
  templateUrl: './sales-snapshot-settings.component.html',
  styleUrls: ['./sales-snapshot-settings.component.scss'],
})
export class SalesSnapshotSettingsComponent implements OnInit {
  configuration$: Observable<ConfigurationInterface> = this.partnerId$.pipe(
    switchMap((partnerId) => this.whitelabelService.getConfiguration(partnerId)),
    filter((config) => !!config),
    publishReplay(1),
    refCount(),
  );

  wizardUrl$: Observable<string> = this.marketId$.pipe(
    map((marketId) => {
      if (marketId !== 'default') {
        return `/customize-design/markets/${marketId}/prospect-workflow/wizard/`;
      }
      return '/customize-design/prospect-workflow/wizard/';
    }),
  );
  salesOrderUrl$: Observable<string> = this.marketId$.pipe(
    map((marketId) => `/customize-design/markets/${marketId}/customize-sales-orders/`),
  );

  canCustomizeSnapshotReport$: Observable<boolean> = this.configuration$.pipe(
    map((config) => config.enabledFeatures.includes(CUSTOMIZE_SNAPSHOT)),
  );
  canCustomizeSaleOrders$: Observable<boolean>;

  columnsToDisplay: string[] = ['settingName', 'currentSettingValue'];
  data$: Observable<SnapshotSettingInterface[]> = combineLatest([this.configuration$, this.wizardUrl$]).pipe(
    map(([config, wizardUrl]) => {
      const data = [];

      // If the partner has access to marketing automation
      if (config.enabledFeatures.includes(MARKETING_AUTOMATION)) {
        data.push({
          textKey: 'CUSTOMIZE_PARTNER.SALES.SNAPSHOT_REPORT.TABLE.OPTION.ACQUISITION_CAMPAIGN',
          url: `${wizardUrl}/acquisitionCampaign`,
          currentValue: ' ---- Missing ---- ',
        });
      }

      data.push(
        {
          textKey: 'CUSTOMIZE_PARTNER.SALES.SNAPSHOT_REPORT.TABLE.OPTION.USER_ACCESS',
          url: `${wizardUrl}/userAccess`,
          currentValue: config.snapshotConfiguration.allowClaimUser ? 'Yes' : 'No',
        },
        {
          textKey: 'CUSTOMIZE_PARTNER.SALES.SNAPSHOT_REPORT.TABLE.OPTION.SNAPSHOT_BANNER',
          url: `${wizardUrl}/snapshotBanner`,
          currentValue: config.snapshotConfiguration.useCustomSnapshotHeader ? 'Custom' : 'Default',
        },
      );

      if (config.snapshotConfiguration.allowClaimUser) {
        data.push(
          {
            textKey: 'CUSTOMIZE_PARTNER.SALES.SNAPSHOT_REPORT.TABLE.OPTION.SNAPSHOT_STICKY_FOOTER',
            url: `${wizardUrl}/snapshotFooter`,
            currentValue: config.snapshotConfiguration.useCustomSnapshotFooter ? 'Custom' : 'Default',
          },
          {
            textKey: 'CUSTOMIZE_PARTNER.SALES.SNAPSHOT_REPORT.TABLE.OPTION.SNAPSHOT_POP_UP',
            url: `${wizardUrl}/snapshotPopup`,
            currentValue: config.snapshotConfiguration.useCustomSnapshotPopupMessage ? 'Custom' : 'Default',
          },
          {
            textKey: 'CUSTOMIZE_PARTNER.SALES.SNAPSHOT_REPORT.TABLE.OPTION.INTRODUCTION_MESSAGE',
            url: `${wizardUrl}/introductionMessage`,
            currentValue: ' ---- Missing ---- ',
          },
        );
      } else {
        data.push(
          {
            textKey: 'CUSTOMIZE_PARTNER.SALES.SNAPSHOT_REPORT.TABLE.OPTION.SNAPSHOT_SLIDE_UP',
            currentValue: 'N/A',
          },
          {
            textKey: 'CUSTOMIZE_PARTNER.SALES.SNAPSHOT_REPORT.TABLE.OPTION.SNAPSHOT_POP_UP',
            currentValue: 'N/A',
          },
          {
            textKey: 'CUSTOMIZE_PARTNER.SALES.SNAPSHOT_REPORT.TABLE.OPTION.INTRODUCTION_MESSAGE',
            currentValue: 'N/A',
          },
        );
      }

      return data;
    }),
  );

  constructor(
    private readonly whitelabelService: WhitelabelService,
    @Inject('PARTNER_ID') readonly partnerId$: Observable<string>,
    @Inject('MARKET_ID') private readonly marketId$: Observable<string>,
    private readonly featureFlagService: FeatureFlagService,
  ) {}

  ngOnInit(): void {
    this.canCustomizeSaleOrders$ = combineLatest([this.partnerId$, this.marketId$]).pipe(
      switchMap(([partnerId, marketId]) =>
        combineLatest([
          this.configuration$,
          this.featureFlagService.checkFeatureFlag(partnerId, marketId, 'sales_order_config'),
        ]),
      ),
      filter(([config, access]) => !!config && typeof access !== 'undefined'),
      map(([config, canAccess]) => config.enabledFeatures.includes(SALES_ORDERS) && canAccess),
    );
  }
}
